import { version } from 'react';

import { payloadPublisher, ExperiencePerformanceTypes, TenantType } from '@atlassian/ufo';

import { getBuildInfo } from '@confluence/build-info';
import type { SessionDataType } from '@confluence/session-data/entry-points/SessionDataTypes.ts';
import { getMark } from '@confluence/performance/entry-points/performance.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

export type GasV3 = {
	sendOperationalEvent: (payload: any) => void;
};

export function allowlistWithFeatureFlagClient(): (key: string) => boolean {
	return function allowlist(key: string) {
		/**
		 * Key formats:
		 *
		 * product  -> {publisher.product}.fe.{experience:type}.{experience:id}
		 *
		 * platform -> platform.fe.{experience:type}.{platform.component}.{experience.id}
		 */
		const keyParts = key.split('.');
		const publisher = keyParts[0];

		function platformAllowlist(component: string, experience: string) {
			/**
			 * UFO for Smart Experiences team components
			 * @returns {boolean}
			 */
			function isASmartComponentExperience(): boolean {
				return (
					['smart-user-picker', 'user-picker', 'mention'].includes(component) &&
					[
						'smart-user-picker-rendered',
						'user-picker-rendered',
						'smart-user-picker-options-shown',
						'user-picker-options-shown',
					].includes(experience)
				);
			}

			/**
			 * Does the platform is a search dialog experience?
			 * @returns {boolean}
			 */
			function isSearchDialogExperience(): boolean {
				const searchComponentPrefix = 'search-';
				return component.startsWith(searchComponentPrefix);
			}

			/**
			 * Does the platform is a media picker event?
			 * @returns {boolean}
			 */
			function isAMediaEvent(): boolean {
				const mediaComponentPrefix = 'media-';

				return component.startsWith(mediaComponentPrefix);
			}

			/**
			 * Determine if the UFO event in the allowed list is sourced from reaction component
			 * @returns {boolean}
			 */
			function isReactionsExperience(): boolean {
				const reactionComponents = ['reactions-picker', 'reactions-list', 'reaction-item'];
				const reactionExperiences = [
					'reactions-rendered',
					'reactions-picker-opened',
					'reaction-added',
					'reaction-details-fetched',
					'reaction-removed',
				];
				return reactionComponents.includes(component) && reactionExperiences.includes(experience);
			}
			/**
			 * Determine if the UFO event in the allowed list is sourced from emoji component
			 * @returns {boolean}
			 */
			function isEmojiEvent(): boolean {
				const emojiComponents = ['emoji', 'emoji-picker', 'emoji-provider'];
				const emojiExperiences = [
					'emoji-rendered',
					'emoji-resource-fetched',
					'emoji-picker-opened',
					'emoji-searched',
					'emoji-uploaded',
					'emoji-selection-recorded',
				];
				return emojiComponents.includes(component) && emojiExperiences.includes(experience);
			}

			/**
			 * Is it a People experience
			 * @returns {boolean}
			 */
			function isPeopleExperience(): boolean {
				if (component === 'people') {
					const allowedPeopleAndTeamsExperiences = [
						'render-team-member-invite-dialog',
						'render-team-member-remove-dialog',
						'remove-member-from-team-action',
						'add-new-member-to-team-action',
						'render-people-nav-menu',
						'team-creation-action',
						'remove-a-member-from-team',
						'invite-members-to-team',
						'render-share-dialog',
						'share-submit-action',
						'membership-control-change',
						'membership-control-change-handle-requests',
						'membership-control-change-change-team-type',
						'search-people-and-teams-page-results',
						'search-people-dropdown-results',
						'search-teams-dropdown-results',
						'search-people-and-teams-dropdown-results',
					];
					return allowedPeopleAndTeamsExperiences.includes(experience);
				}
				return false;
			}

			/**
			 * Is it an Invite People experience
			 * @returns {boolean}
			 */
			function isInvitePeopleExperience(): boolean {
				if (component === 'invite-people') {
					const allowedInvitePeopleExperiences = [
						'invite-form-submitted',
						'invite-people-rendered',
						'teams-invite-section-experience',
					];
					return allowedInvitePeopleExperiences.includes(experience);
				}
				return false;
			}

			/**
			 * Does the platform is a notification experience?
			 * @returns {boolean}
			 */
			function isNotificationListExperience(): boolean {
				if (component === 'notificationList') {
					const allowedExperiences = [
						'notification-list-render-notifications-without-content',
						'notification-list-render-notifications-with-content',
					];
					return allowedExperiences.includes(experience);
				}
				return false;
			}

			/**
			 * Determines if the UFO event in the allowed list is sourced from smart link component
			 */
			function isASmartLinkExperience(): boolean {
				return (
					component === 'smart-links' &&
					[
						'smart-link-rendered',
						'smart-link-authenticated',
						'smart-link-action-invocation',
						'smart-link-ai-summary',
					].includes(experience)
				);
			}

			function isLinkPickerExperience(): boolean {
				return component === 'link-picker';
			}

			function isDatasourceExperience(): boolean {
				return component === 'datasource';
			}

			/**
			 * Is it an Atlassian Switcher experience
			 * @returns {boolean}
			 */
			function isAtlassianSwitcherExperience(): boolean {
				return component === 'atlassianSwitcher';
			}

			function isPlatformProductExperience(): boolean {
				const allowedPlatformComponents = [
					'ai-speech-to-text',
					'ai-snippet',
					'audio-player',
					'enterprise-her',
				];
				return allowedPlatformComponents.includes(component);
			}

			function isEditorExperience(): boolean {
				return component === 'editor';
			}

			return (
				isASmartComponentExperience() ||
				isEmojiEvent() ||
				isReactionsExperience() ||
				isAMediaEvent() ||
				isSearchDialogExperience() ||
				isNotificationListExperience() ||
				isPeopleExperience() ||
				isASmartLinkExperience() ||
				isLinkPickerExperience() ||
				isInvitePeopleExperience() ||
				isDatasourceExperience() ||
				isAtlassianSwitcherExperience() ||
				isPlatformProductExperience() ||
				isEditorExperience()
			);
		}

		function confluenceAllowlist() {
			// only confluence with defined perf metrics
			return !key.includes('UNKNOWN');
		}

		switch (publisher) {
			case 'confluence':
				return confluenceAllowlist();
			case 'platform':
				// platform -> platform.fe.{experience:type}.{platform.component}.{experience.id}
				const [, , , component, experience] = keyParts;
				return platformAllowlist(component, experience);
			default:
				return false;
		}
	};
}

export const ssrConfig = () => ({
	getFeatureFlags: () => ({}),
	getDoneMark: () => getMark('CFP-63.ssr-ttr') ?? null,
	getTimings: () => (window as any).__SSR_MEASURES__ || {},
});

async function setup(sessionData: SessionDataType, getAnalyticsWebClientPromise: Promise<GasV3>) {
	const isSynthetic = StatsigConfigurations.getBoolean(
		'confluence_frontend_pollinator_synthetic_tenants',
		false,
	);
	const gasv3 = await getAnalyticsWebClientPromise;
	payloadPublisher.setup({
		product: 'confluence',
		app: {
			version: {
				web: getBuildInfo().FRONTEND_VERSION,
				ssr: window.__SSR_BUILD__,
			},
			framework: { name: 'react', version },
		},
		region: sessionData.shard || 'unknown',
		tenantType: isSynthetic ? TenantType.Synthetic : TenantType.RealUser,
		gasv3,
		featureFlags: [],
		featureFlagClient: { getValue: () => false },
		plugins: [],
		ssr: ssrConfig(),
		performanceConfig: {
			histogram: {
				[ExperiencePerformanceTypes.PageLoad]: {
					initial: {
						fmp: '2500_3000_3500_4000_5000_6000',
						tti: '4000_4500_5000_6000_7000_8000_9000',
					},
					transition: {
						fmp: '2500_3000_3500_4000_5000_6000',
						tti: '2500_3000_3500_4000_5000_6000',
					},
				},
				[ExperiencePerformanceTypes.InlineResult]: {
					result: '100_200_500_1000_1500',
					response: '50_100_150_200_300_500',
				},
				[ExperiencePerformanceTypes.Custom]: {
					duration: '250_500_1000_1500_2000_3000_4000',
				},
			},
		},
		allowlist: allowlistWithFeatureFlagClient(),
	});
}

export { setup };
